export type NotificationSource =
  | 'radios'
  | 'usuarios'
  | 'pagos'
  | 'planes'
  | 'marketplace'
  | 'publicidad'
  | 'streaming'
  | 'storage'
  | 'soporte'
  | 'ia'
  | 'backups'
  | 'errores';

export type NotificationSeverity = 'info' | 'warning' | 'error' | 'critical';

export type NotificationFilterType = NotificationSeverity | 'sistema' | 'usuario' | 'radio' | 'integracion';

export interface SystemNotification {
  id: number;
  source: NotificationSource;
  severity: NotificationSeverity;
  title: string;
  message: string;
  read: boolean;
  important: boolean;
  archived: boolean;
  createdAt: string;
  actionLabel?: string;
  actionPath?: string;
  metadata?: {
    affectedUser?: string;
    affectedRadio?: string;
    errorCode?: string;
    transactionId?: string;
    planName?: string;
    storageUsed?: string;
    aiModel?: string;
  };
}

export const notificationSourceMeta: Record<NotificationSource, { label: string; icon: string; color: string }> = {
  radios:      { label: 'Radios',       icon: 'ri-radio-line',               color: 'text-amber-600' },
  usuarios:    { label: 'Usuarios',     icon: 'ri-user-line',                color: 'text-sky-600' },
  pagos:       { label: 'Pagos',        icon: 'ri-money-dollar-circle-line', color: 'text-emerald-600' },
  planes:      { label: 'Planes',       icon: 'ri-stack-line',               color: 'text-violet-600' },
  marketplace: { label: 'Marketplace',  icon: 'ri-store-2-line',             color: 'text-rose-600' },
  publicidad:  { label: 'Publicidad',   icon: 'ri-advertisement-line',       color: 'text-orange-600' },
  streaming:   { label: 'Streaming',    icon: 'ri-live-line',                color: 'text-red-600' },
  storage:     { label: 'Storage',      icon: 'ri-hard-drive-2-line',        color: 'text-indigo-600' },
  soporte:     { label: 'Soporte',      icon: 'ri-customer-service-2-line',  color: 'text-teal-600' },
  ia:          { label: 'IA',           icon: 'ri-robot-line',               color: 'text-fuchsia-600' },
  backups:     { label: 'Backups',      icon: 'ri-cloud-line',               color: 'text-cyan-600' },
  errores:     { label: 'Errores',      icon: 'ri-error-warning-line',       color: 'text-red-600' },
};

export const severityMeta: Record<NotificationSeverity, { label: string; icon: string; bg: string; textColor: string; badgeBg: string }> = {
  critical: { label: 'Crítica',    icon: 'ri-alert-fill',         bg: 'bg-red-500',     textColor: 'text-red-600',   badgeBg: 'bg-red-50' },
  error:    { label: 'Error',      icon: 'ri-close-circle-fill',  bg: 'bg-red-400',     textColor: 'text-red-500',   badgeBg: 'bg-red-50' },
  warning:  { label: 'Advertencia',icon: 'ri-error-warning-fill', bg: 'bg-amber-400',   textColor: 'text-amber-600', badgeBg: 'bg-amber-50' },
  info:     { label: 'Información',icon: 'ri-information-fill',   bg: 'bg-sky-400',     textColor: 'text-sky-600',   badgeBg: 'bg-sky-50' },
};

export const filterTypeMeta: Record<NotificationFilterType, { label: string; icon: string }> = {
  critical:    { label: 'Crítica',      icon: 'ri-alert-fill' },
  error:       { label: 'Error',        icon: 'ri-close-circle-fill' },
  warning:     { label: 'Advertencia',  icon: 'ri-error-warning-fill' },
  info:        { label: 'Información',  icon: 'ri-information-fill' },
  sistema:     { label: 'Sistema',      icon: 'ri-server-line' },
  usuario:     { label: 'Usuario',      icon: 'ri-user-line' },
  radio:       { label: 'Radio',        icon: 'ri-radio-line' },
  integracion: { label: 'Integración',  icon: 'ri-puzzle-line' },
};

export const allNotificationSources: NotificationSource[] = [
  'radios', 'usuarios', 'pagos', 'planes', 'marketplace', 'publicidad',
  'streaming', 'storage', 'soporte', 'ia', 'backups', 'errores',
];

export const allFilterTypes: NotificationFilterType[] = [
  'info', 'warning', 'error', 'critical', 'sistema', 'usuario', 'radio', 'integracion',
];

export const globalNotifications: SystemNotification[] = [
  // ─── CRITICAL ───
  {
    id: 1, source: 'errores', severity: 'critical',
    title: 'Falla crítica en cluster de streaming',
    message: 'El nodo principal del cluster de streaming (icecast-01) dejó de responder. Se activó failover automático al nodo secundario. Latencia incrementada en un 340%. Se requiere intervención inmediata del equipo de infraestructura.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 8).toISOString(),
    actionLabel: 'Ver System Health', actionPath: '/sa/system-health-center',
    metadata: { errorCode: 'ERR-STREAM-CLUSTER-001', affectedRadio: 'icecast-01' },
  },
  {
    id: 2, source: 'pagos', severity: 'critical',
    title: 'Fallo masivo en procesamiento de pagos',
    message: 'Stripe reportó 47 transacciones fallidas en los últimos 30 minutos. Error de conectividad con el gateway. Se notificó al equipo de finanzas. Impacto estimado: $3,240 USD en renovaciones no procesadas.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 32).toISOString(),
    actionLabel: 'Ver Pagos', actionPath: '/sa/payments',
    metadata: { errorCode: 'ERR-PAY-GTW-047', transactionId: 'BATCH-20260712-A' },
  },
  {
    id: 3, source: 'backups', severity: 'critical',
    title: 'Backup automático falló — base de datos principal',
    message: 'El backup programado de la base de datos principal (rfw_production) falló a las 03:00 UTC. Error: espacio insuficiente en destino S3. Se requiere liberar almacenamiento y ejecutar backup manual.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    actionLabel: 'Ver Storage', actionPath: '/sa/storage',
    metadata: { errorCode: 'ERR-BKP-STORAGE-FULL' },
  },

  // ─── ERROR ───
  {
    id: 4, source: 'streaming', severity: 'error',
    title: 'Múltiples streams caídos en región AR',
    message: '3 radios en Argentina reportaron caída de stream simultánea: Radio Brisa (89.10), FM Estelar (94.30) y DigitalBeat (102.70). Se investiga posible causa en CDN regional.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
    actionLabel: 'Ver Radio Health', actionPath: '/sa/radio-health',
    metadata: { affectedRadio: '89.10, 94.30, 102.70' },
  },
  {
    id: 5, source: 'ia', severity: 'error',
    title: 'OpenAI API: rate limit excedido',
    message: 'El endpoint de recomendaciones IA excedió el rate limit de OpenAI. 124 requests rechazadas en la última hora. Se activó circuit breaker y se está sirviendo cache estático.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 55).toISOString(),
    actionLabel: 'Ver integraciones', actionPath: '/sa/integrations',
    metadata: { aiModel: 'GPT-4o', errorCode: 'RATE-LIMIT-429' },
  },
  {
    id: 6, source: 'soporte', severity: 'error',
    title: 'Sistema de tickets colapsado',
    message: 'La cola de soporte superó los 80 tickets abiertos. Tiempo promedio de respuesta: 14 horas (SLA: 4h). Se requiere refuerzo del equipo de soporte.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    actionLabel: 'Ver Soporte', actionPath: '/sa/support',
  },
  {
    id: 7, source: 'storage', severity: 'error',
    title: 'Almacenamiento crítico en región principal',
    message: 'El bucket de storage principal alcanzó el 94.8% de capacidad (892 GB / 950 GB). 6 radios no pueden subir nuevos archivos. Se requiere ampliación urgente.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 40).toISOString(),
    actionLabel: 'Ver Storage', actionPath: '/sa/storage',
    metadata: { storageUsed: '94.8%', affectedRadio: '6 radios afectadas' },
  },

  // ─── WARNING ───
  {
    id: 8, source: 'planes', severity: 'warning',
    title: '14 planes Premium por vencer esta semana',
    message: 'Catorce radios con plan Premium anual vencen entre el 14 y el 18 de julio. Total renovaciones pendientes: $8,260 USD. Se recomienda campaña de recordatorio.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
    actionLabel: 'Ver Renovaciones', actionPath: '/sa/billing/renewals',
    metadata: { planName: 'Premium Anual', affectedRadio: '14 radios' },
  },
  {
    id: 9, source: 'radios', severity: 'warning',
    title: 'Radio sin transmisión por más de 24h',
    message: 'Radio Onda Retro (97.50) no ha transmitido en las últimas 24 horas. Última señal detectada: 2026-07-11 08:15. Se inicia protocolo de verificación.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 1).toISOString(),
    actionLabel: 'Ver Radio', actionPath: '/sa/radios',
    metadata: { affectedRadio: '97.50 RFW' },
  },
  {
    id: 10, source: 'usuarios', severity: 'warning',
    title: 'Pico inusual de registros desde IP rango sospechoso',
    message: 'Se detectaron 89 registros en 2 horas desde el rango IP 185.220.x.x. Posible ataque de creación masiva de cuentas. Se aplicó rate limiting temporal.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
    actionLabel: 'Ver Auditoría', actionPath: '/sa/global-audit-center',
    metadata: { affectedUser: 'rango 185.220.x.x', errorCode: 'SUSPICIOUS-REG-089' },
  },
  {
    id: 11, source: 'publicidad', severity: 'warning',
    title: 'Campaña publicitaria con rendimiento bajo',
    message: 'La campaña "Summer Hits 2026" (ID: #CAMP-442) tiene CTR 0.08%, muy por debajo del promedio de 0.42%. Se recomienda revisar creatividades y segmentación.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    actionLabel: 'Ver Publicidad', actionPath: '/sa/ads',
    metadata: { transactionId: 'CAMP-442' },
  },
  {
    id: 12, source: 'marketplace', severity: 'warning',
    title: 'Disputa abierta en marketplace — 72h sin resolver',
    message: 'Transacción #MKT-2341 entre Radio Éxitos TV y AudioPro sigue en disputa tras 72 horas. Motivo: diferencias en métricas de entrega. Intervención requerida.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
    actionLabel: 'Ver Marketplace', actionPath: '/sa/ad-marketplace',
    metadata: { transactionId: 'MKT-2341' },
  },

  // ─── INFO ───
  {
    id: 13, source: 'radios', severity: 'info',
    title: 'Nueva radio verificada: Radio Horizonte 360',
    message: 'Radio Horizonte 360 completó el proceso de verificación. Frecuencia asignada: 106.10 RFW. Plan: Premium. Lista para operar desde hoy.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    actionLabel: 'Ver Radio', actionPath: '/sa/radios',
    metadata: { affectedRadio: '106.10 RFW', planName: 'Premium' },
  },
  {
    id: 14, source: 'pagos', severity: 'info',
    title: 'Facturación de julio 2026 completada',
    message: 'Se generaron y enviaron 312 facturas correspondientes a julio 2026. Total facturado: $31,840 USD. 94% de cobro automatizado exitoso.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
    actionLabel: 'Ver Facturación', actionPath: '/sa/billing',
  },
  {
    id: 15, source: 'ia', severity: 'info',
    title: 'Nuevo modelo IA desplegado: recomendaciones v2.1',
    message: 'Se desplegó la versión 2.1 del modelo de recomendaciones. Mejora del 18% en precisión de sugerencias de radios. Basado en fine-tuning con datos de Q2 2026.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    actionLabel: 'Ver IA Settings', actionPath: '/sa/ai-radio',
    metadata: { aiModel: 'Recomendador v2.1' },
  },
  {
    id: 16, source: 'backups', severity: 'info',
    title: 'Backup semanal completado exitosamente',
    message: 'Backup completo de base de datos, assets y configuraciones finalizado. Tamaño: 12.4 GB comprimidos. Réplica en región secundaria (US-East) verificada.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 20).toISOString(),
    actionLabel: 'Ver Backups', actionPath: '/sa/backups',
  },
  {
    id: 17, source: 'planes', severity: 'info',
    title: 'Nuevo plan Enterprise lanzado',
    message: 'El plan Enterprise ya está disponible. Incluye: streaming ilimitado, storage 500GB, soporte 24/7 dedicado, API access completo. Precio: $299/mes.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 10).toISOString(),
    actionLabel: 'Ver Planes', actionPath: '/sa/plans',
    metadata: { planName: 'Enterprise' },
  },
  {
    id: 18, source: 'streaming', severity: 'info',
    title: 'CDN optimizado — latencia reducida 22%',
    message: 'La migración al nuevo edge node en São Paulo redujo la latencia promedio de streaming en Sudamérica de 180ms a 140ms. 612 radios beneficiadas.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 16).toISOString(),
    actionLabel: 'Ver Streaming', actionPath: '/sa/broadcasters',
  },
  {
    id: 19, source: 'usuarios', severity: 'info',
    title: 'Hito alcanzado: 50,000 oyentes activos',
    message: 'La plataforma superó los 50,000 oyentes activos simultáneos por primera vez. Pico registrado a las 21:34 UTC. Récord anterior: 47,200 (junio 2026).',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 1).toISOString(),
    actionLabel: 'Ver Analytics', actionPath: '/sa/analytics',
  },
  {
    id: 20, source: 'publicidad', severity: 'info',
    title: 'Revenue publicitario de junio: $24,500 USD',
    message: 'Resumen junio 2026: 89 campañas activas, 2.1M impresiones servidas, CTR promedio 0.41%. Top anunciante: MegaStore S.A. ($3,200).',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    actionLabel: 'Ver Ads', actionPath: '/sa/ads',
  },

  // ─── MORE ───
  {
    id: 21, source: 'soporte', severity: 'info',
    title: 'Ticket de alta prioridad resuelto',
    message: 'Ticket #TKT-8921 de Radio StereoPlus fue resuelto por el equipo de soporte. Tema: configuración de dominio personalizado. Tiempo de resolución: 2.3 horas.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 14).toISOString(),
    actionLabel: 'Ver Tickets', actionPath: '/sa/support',
  },
  {
    id: 22, source: 'storage', severity: 'info',
    title: 'Limpieza automática de archivos temporales',
    message: 'El job de limpieza semanal eliminó 3.2 GB de archivos temporales y caché expirado. 842 archivos eliminados de 14 buckets.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 30).toISOString(),
    actionLabel: 'Ver Storage', actionPath: '/sa/storage',
  },
  {
    id: 23, source: 'errores', severity: 'warning',
    title: 'Incremento de errores 500 en API de búsqueda',
    message: 'La API de búsqueda global registró 34 errores 500 en la última hora. Tasa de error: 2.1% (umbral: 0.5%). Se investiga posible degradación de ElasticSearch.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    actionLabel: 'Ver System Health', actionPath: '/sa/system-health-center',
    metadata: { errorCode: 'ERR-API-500' },
  },
  {
    id: 24, source: 'marketplace', severity: 'info',
    title: 'Nuevo anunciante corporate verificado',
    message: 'AudioPro S.A. completó la verificación corporate en marketplace. Límite de gasto mensual: $15,000. Ya puede publicar ofertas de patrocinio.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
    actionLabel: 'Ver Marketplace', actionPath: '/sa/commercial-marketplace',
    metadata: { affectedUser: 'AudioPro S.A.' },
  },
  {
    id: 25, source: 'planes', severity: 'warning',
    title: 'Usuario solicita downgrade de Enterprise a Premium',
    message: 'Radio Global Beat solicitó downgrade de Enterprise a Premium. Motivo: reducción de operaciones. Impacto financiero: -$199/mes. Pendiente de aprobación.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 7).toISOString(),
    actionLabel: 'Gestionar Plan', actionPath: '/sa/plans',
    metadata: { affectedRadio: 'Radio Global Beat', planName: 'Enterprise → Premium' },
  },
  {
    id: 26, source: 'errores', severity: 'critical',
    title: 'Certificado SSL expirado en subdominio',
    message: 'El certificado SSL de api.rfw.app expiró hace 15 minutos. Todas las llamadas a la API pública están fallando con errores de certificado. Se requiere renovación urgente.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    actionLabel: 'Ver Dominios', actionPath: '/sa/premium-domains',
    metadata: { errorCode: 'SSL-EXPIRED-001' },
  },
  {
    id: 27, source: 'radios', severity: 'info',
    title: 'Radio del mes: FM Estelar 94.30',
    message: 'FM Estelar 94.30 fue seleccionada como Radio del Mes de Julio. Métricas: 4,200 oyentes promedio, 98.7% uptime, 12 programas activos.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    actionLabel: 'Ver Radio', actionPath: '/sa/radios',
    metadata: { affectedRadio: '94.30 RFW' },
  },
  {
    id: 28, source: 'usuarios', severity: 'info',
    title: 'Nuevo locutor verificado: Martín Delgado',
    message: 'El locutor Martín Delgado completó la verificación de identidad. Ya puede ser asignado a programas en cualquier radio de la plataforma.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
    metadata: { affectedUser: 'Martín Delgado' },
  },
  {
    id: 29, source: 'pagos', severity: 'warning',
    title: 'Comisión de plataforma pendiente de distribución',
    message: 'La comisión de plataforma de junio ($4,280 USD) está pendiente de distribución a los payment processors. Fecha límite: 15 de julio.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 9).toISOString(),
    actionLabel: 'Ver Pagos', actionPath: '/sa/payouts',
  },
  {
    id: 30, source: 'streaming', severity: 'info',
    title: 'Nuevo codec AAC+ habilitado en cluster principal',
    message: 'Se habilitó el codec AAC+ v2 en el cluster de streaming principal. Mejora de calidad de audio a bitrates más bajos. 128kbps suena como 192kbps MP3.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 50).toISOString(),
    actionLabel: 'Ver Streaming', actionPath: '/sa/broadcasters',
  },
  {
    id: 31, source: 'backups', severity: 'warning',
    title: 'Backup de archivos multimedia atrasado 3 días',
    message: 'El backup incremental de archivos multimedia (fotos, videos) no se ejecuta desde hace 3 días. Causa: job scheduler en nodo secundario detenido.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    actionLabel: 'Ver System Health', actionPath: '/sa/system-health-center',
  },
  {
    id: 32, source: 'ia', severity: 'warning',
    title: 'Modelo de moderación IA: falsos positivos en aumento',
    message: 'El modelo de moderación automática de chat aumentó falsos positivos al 4.2% (umbral: 2%). Se recomienda ajustar threshold o fine-tuning con nuevos datos.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 11).toISOString(),
    actionLabel: 'Ver IA Settings', actionPath: '/sa/integrations',
    metadata: { aiModel: 'Moderación v3.1', errorCode: 'FP-RATE-4.2%' },
  },
  {
    id: 33, source: 'soporte', severity: 'warning',
    title: 'SLA de soporte incumplido para 6 tickets Premium',
    message: 'Seis tickets de radios Premium superaron el SLA de 4 horas. Tickets más antiguo: 9h 23min. Se requiere acción del team lead de soporte.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 1).toISOString(),
    actionLabel: 'Ver Soporte', actionPath: '/sa/support',
  },
  {
    id: 34, source: 'publicidad', severity: 'info',
    title: 'Nuevo formato publicitario: Audio Pre-Roll 15s',
    message: 'Se lanzó el formato Audio Pre-Roll de 15 segundos. Ya disponible para todas las campañas. 38 anunciantes lo activaron en las primeras 24 horas.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    actionLabel: 'Ver Ads', actionPath: '/sa/ads',
  },
  {
    id: 35, source: 'marketplace', severity: 'error',
    title: 'Transacción marketplace fallida por fondos insuficientes',
    message: 'Transacción #MKT-2398 de $850 entre BeverageCo y Radio Urbana falló. Motivo: fondos insuficientes en wallet del comprador. Se notificó a ambas partes.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
    actionLabel: 'Ver Marketplace', actionPath: '/sa/ad-marketplace',
    metadata: { transactionId: 'MKT-2398', affectedUser: 'BeverageCo' },
  },
  {
    id: 36, source: 'storage', severity: 'warning',
    title: 'Radio excedió cuota de storage del plan',
    message: 'Radio DigitalBeats (Plan Pro, cuota 50GB) alcanzó 51.2 GB. Subidas bloqueadas hasta que libere espacio o actualice a Premium (100GB).',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
    actionLabel: 'Ver Storage', actionPath: '/sa/storage',
    metadata: { affectedRadio: 'DigitalBeats', planName: 'Pro', storageUsed: '51.2GB / 50GB' },
  },
  {
    id: 37, source: 'usuarios', severity: 'info',
    title: 'Programa de embajadores: 3 nuevos miembros',
    message: 'Tres oyentes fueron aceptados en el programa de embajadores RFW: Laura Méndez, Carlos Ruiz y Ana Vega. Total embajadores activos: 47.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 22).toISOString(),
    metadata: { affectedUser: '3 nuevos embajadores' },
  },
  {
    id: 38, source: 'errores', severity: 'error',
    title: 'Worker de notificaciones push detenido',
    message: 'El worker de notificaciones push (Firebase Cloud Messaging) dejó de procesar mensajes. 2,340 notificaciones en cola. Último heartbeat: hace 18 minutos.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
    actionLabel: 'Ver System Health', actionPath: '/sa/system-health-center',
    metadata: { errorCode: 'WORKER-FCM-DOWN' },
  },
  {
    id: 39, source: 'planes', severity: 'info',
    title: 'Promoción de verano activada',
    message: 'Promoción "Verano 2026": 20% de descuento en planes Premium y Enterprise para nuevas radios. Válido hasta el 31 de agosto. Código: SUMMER26.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    actionLabel: 'Ver Planes', actionPath: '/sa/plans',
    metadata: { planName: 'Promo Verano 2026' },
  },
  {
    id: 40, source: 'radios', severity: 'warning',
    title: 'Radio solicitó cambio de frecuencia',
    message: 'Radio Clásica FM (actual 88.90) solicitó cambio a frecuencia 101.30. La frecuencia destino está disponible. Se requiere revisión y aprobación.',
    read: false, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
    actionLabel: 'Ver Frequencias', actionPath: '/sa/frequencies',
    metadata: { affectedRadio: '88.90 → 101.30' },
  },
  {
    id: 41, source: 'streaming', severity: 'critical',
    title: 'Ataque DDoS detectado en endpoint de streaming',
    message: 'Se detectó un ataque DDoS volumétrico (12 Gbps) dirigido al endpoint de streaming público. Cloudflare activó mitigación automática. Tráfico legítimo restaurado al 94%.',
    read: false, important: true, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(),
    actionLabel: 'Ver System Health', actionPath: '/sa/system-health-center',
    metadata: { errorCode: 'DDOS-MITIGATED' },
  },
  {
    id: 42, source: 'pagos', severity: 'info',
    title: 'Integración con nuevo payment processor: MercadoPago',
    message: 'MercadoPago fue integrado como nuevo procesador de pagos para Latinoamérica. Ya disponible para radios en Argentina, México, Brasil y Chile.',
    read: true, important: false, archived: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 26).toISOString(),
    actionLabel: 'Ver Procesadores', actionPath: '/sa/payment-processors',
  },
];